import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Checkbox, Popconfirm, Space, Tag, Tooltip, Typography, message } from 'antd';
import { QuestionCircleOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../i18n';
import hostSharedService from '../../../services/hostSharedService';

const STATUS_COLOR = {
    pending: 'processing',
    processing: 'processing',
    issued: 'processing',
    active: 'success',
    disabled: 'default',
    failed: 'error',
};

/**
 * 共享主机 Let's Encrypt 免费证书(申请 / 状态 / 停用)
 */
const LetsEncryptPanel = ({ vhostId, onChanged }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState([]);
    const [busy, setBusy] = useState(false);
    const timer = useRef(null);
    const lastStatus = useRef(null);

    const statusText = {
        pending: t('Queued'),
        processing: t('Issuing'),
        issued: t('Deploying'),
        active: t('Enabled'),
        disabled: t('Disabled'),
        failed: t('Failed'),
    };

    const load = async () => {
        const res = await hostSharedService.getLetsEncrypt(vhostId);
        if (!res.success) {
            setData({ unavailable: res.error || t('Currently unavailable') });
            return;
        }
        setData(res.data);
        const cert = res.data.certificate;
        setSelected((prev) => (prev.length ? prev : (cert?.domains?.length ? cert.domains : res.data.domains || [])));
        clearTimeout(timer.current);
        const wasWorking = ['pending', 'processing', 'issued'].includes(lastStatus.current);
        lastStatus.current = cert?.status || null;
        if (cert && ['pending', 'processing', 'issued'].includes(cert.status)) {
            timer.current = setTimeout(load, 10000);
        } else if (wasWorking && cert?.status === 'active') {
            onChanged && onChanged();
        }
    };

    useEffect(() => {
        if (vhostId) load();
        return () => clearTimeout(timer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vhostId]);

    const apply = async () => {
        if (!selected.length) return;
        setBusy(true);
        const res = await hostSharedService.requestLetsEncrypt(vhostId, selected);
        setBusy(false);
        if (res.success) {
            message.success(res.message || t('Submitted'));
            load();
        } else {
            message.error(res.error || t('Request failed'));
        }
    };

    const disable = async (remove) => {
        setBusy(true);
        const res = await hostSharedService.disableLetsEncrypt(vhostId, remove);
        setBusy(false);
        if (res.success) {
            message.success(res.message || t('Operation successful'));
            load();
            onChanged && onChanged();
        } else {
            message.error(res.error || t('Operation failed'));
        }
    };

    if (!data) return <Card size="small" loading style={{ marginBottom: 16 }} />;
    if (data.unavailable) return null;

    const cert = data.certificate;
    const working = cert && ['pending', 'processing', 'issued'].includes(cert.status);

    return (
        <Card
            size="small"
            style={{ marginBottom: 16 }}
            title={
                <Space>
                    <SafetyCertificateOutlined />
                    {t("Free certificate (Let's Encrypt)")}
                    <Tooltip title={t('The domain must resolve to this host; the certificate renews automatically after issuance')}>
                        <QuestionCircleOutlined style={{ color: '#999' }} />
                    </Tooltip>
                    {cert && <Tag color={STATUS_COLOR[cert.status]}>{statusText[cert.status] || cert.status}</Tag>}
                    {(cert?.staging || data.staging) && <Tag color="warning">{t('Test certificate')}</Tag>}
                </Space>
            }
            extra={<Button size="small" type="text" icon={<ReloadOutlined />} onClick={load} />}
        >
            {data.domains?.length ? (
                <Checkbox.Group
                    options={data.domains.map((d) => ({ label: d, value: d }))}
                    value={selected}
                    onChange={setSelected}
                    disabled={working}
                />
            ) : (
                <Typography.Text type="secondary">{t('Please bind a domain first')}</Typography.Text>
            )}

            {cert?.expires_at && cert.status === 'active' && (
                <div style={{ marginTop: 8 }}>
                    <Typography.Text type="secondary">
                        {t('Expires')}: {cert.expires_at.slice(0, 10)}
                        {cert.auto_renew ? ` · ${t('Auto-renewal')}` : ''}
                    </Typography.Text>
                </div>
            )}
            {cert?.last_error && !working && cert.status !== 'active' && (
                <div style={{ marginTop: 8 }}>
                    <Typography.Text type="danger" ellipsis={{ tooltip: cert.last_error }} style={{ maxWidth: '100%' }}>
                        {cert.last_error}
                    </Typography.Text>
                </div>
            )}

            <Space style={{ marginTop: 12 }} wrap>
                <Button type="primary" loading={busy || working} disabled={!selected.length} onClick={apply}>
                    {cert && cert.status === 'active' ? t('Reissue') : t('Request certificate')}
                </Button>
                {cert && ['active', 'failed', 'issued'].includes(cert.status) && (
                    <>
                        <Popconfirm title={t('Disable HTTPS?')} onConfirm={() => disable(false)}>
                            <Button disabled={busy}>{t('Disable')}</Button>
                        </Popconfirm>
                        <Popconfirm title={t('Delete the certificate?')} onConfirm={() => disable(true)}>
                            <Button danger disabled={busy}>{t('Delete')}</Button>
                        </Popconfirm>
                    </>
                )}
            </Space>
        </Card>
    );
};

export default LetsEncryptPanel;
